import { get, put } from "../client";

// עוזר קולי (voice assistant) — dashboard (businessAuth) service wrappers.
// Backend contract: Backend/src/routes/v1/voice.route.js + voice.controller.js.
// req.business.sub = the business _id (derived from the token, never sent).
export default {
  // GET → { enabled, ratePerMinute, freeUntil, platformMode }
  // platformMode is the super-admin master switch: "off" | "per_business" | "all".
  // `enabled` is the business's OWN flag for the outbound confirmation call.
  get_assistant_status: async () => get("/voice/assistant-status"),

  // PUT { enabled } → { ok, enabled }. Owners may toggle only their own flag —
  // the per-minute rate and the free period stay super-admin only.
  set_assistant_enabled: async (enabled) =>
    put("/voice/assistant-enabled", { enabled: !!enabled }),

  // GET → { success, conversationToken, dynamicVars }
  // NOTE: this call has a SERVER-SIDE COST — for a business that isn't
  // subscribed to the add-on it consumes one free voice use before the token is
  // minted. Never call it "just to check": only call it when a session is
  // genuinely about to start.
  // Failure modes: 403 "העוזר הקולי כבוי כרגע" (platform master switch off),
  // 402 { code: "voice_locked", entitlement } (free uses spent),
  // 502 "שירות הקול אינו זמין כרגע" (ElevenLabs unreachable).
  get_conversation_token: async () => get("/voice/convai/signed-url"),
};

// The exact sentence the server answers with when the platform master switch
// is off. Kept here so the screen shows the same wording even if a response
// body ever arrives without a message.
export const VOICE_OFF_MESSAGE = "העוזר הקולי כבוי כרגע";

/**
 * Turn an axios failure from any of the calls above into something the UI can
 * show honestly. Always prefers the server's own Hebrew message; the fallbacks
 * only cover the case where the body is empty (proxy error page, 404 from a
 * backend that hasn't been deployed yet, network drop).
 *
 * → { kind, message, status, entitlement }
 *   kind: "platform_off" | "locked" | "service_down" | "auth" | "not_deployed"
 *       | "network" | "unknown"
 */
export function describeVoiceError(error) {
  const response = error && error.response;
  const status = (response && response.status) || 0;
  const body = (response && response.data) || {};
  const serverMessage =
    typeof body.message === "string" && body.message.trim()
      ? body.message.trim()
      : "";

  if (!response) {
    return {
      kind: "network",
      status: 0,
      message: "אין חיבור לשרת. בדקו את החיבור ונסו שוב.",
      entitlement: null,
    };
  }
  if (status === 401) {
    return {
      kind: "auth",
      status,
      message: serverMessage || "פג תוקף ההתחברות — התחברו מחדש.",
      entitlement: null,
    };
  }
  if (status === 403) {
    return {
      kind: "platform_off",
      status,
      message: serverMessage || VOICE_OFF_MESSAGE,
      entitlement: null,
    };
  }
  if (status === 402 || body.code === "voice_locked") {
    return {
      kind: "locked",
      status,
      message: serverMessage || "נוצלו כל השימושים החינמיים בעוזר הקולי",
      entitlement: body.entitlement || null,
    };
  }
  // 404/501 → the endpoint isn't on this environment yet (committed but not
  // deployed). The screen hides the dependent card instead of showing junk.
  if (status === 404 || status === 501) {
    return {
      kind: "not_deployed",
      status,
      message: serverMessage || "השירות אינו זמין בסביבה הזו",
      entitlement: null,
    };
  }
  if (status === 502 || status === 503 || status === 504) {
    return {
      kind: "service_down",
      status,
      message: serverMessage || "שירות הקול אינו זמין כרגע. נסו שוב בעוד רגע.",
      entitlement: null,
    };
  }
  return {
    kind: "unknown",
    status,
    message: serverMessage || "שגיאה בשירות הקולי",
    entitlement: null,
  };
}
